/**
 * FRW Perfect Fit V3 — Elite size recommendation engine.
 * Two paths: Quiz (height → shape → editable measurements → fit) or Camera (2-photo + measurements).
 * No weight needed — uses height + body shape lookup tables, user verifies/edits measurements.
 * Both feed the same matching engine → variant auto-select.
 * Zero bundled deps. IIFE pattern. localStorage profile persists 90 days.
 * Camera: lazy-loads @mediapipe/tasks-vision (~10MB) on first use.
 *
 * Reads product data from #pf-root data attributes:
 *   data-chart: size chart key (womens, mens_jersey, mens_tee, oversized_tee)
 *   data-rec:   fit recommendation (size_up, true_to_size, leans_small, etc.)
 *   data-stretch: stretch level (1-5)
 *   data-fabric: fabric display name
 */
(function () {
  'use strict';

  var KEY = 'frw_perfect_fit';
  var EXPIRY_DAYS = 90;

  // =========================================================================
  // Analytics
  // =========================================================================
  function track(event, data) {
    if (window.dataLayer) window.dataLayer.push(Object.assign({ event: event }, data || {}));
  }

  // =========================================================================
  // Size chart lookup tables (from sizing_insights_database.json)
  // =========================================================================
  var CHARTS = {
    womens: {
      XS:  { bust: [28, 30], waist: [24, 26], hip: [32, 34] },
      S:   { bust: [31, 33], waist: [27, 29], hip: [35, 37] },
      M:   { bust: [33, 36], waist: [30, 32], hip: [38, 40] },
      L:   { bust: [37, 39], waist: [33, 35], hip: [41, 43] },
      XL:  { bust: [40, 42], waist: [36, 38], hip: [44, 46] },
      XXL: { bust: [43, 45], waist: [39, 41], hip: [47, 49] },
      '1X': { bust: [46, 48], waist: [42, 44], hip: [50, 53] },
      '2X': { bust: [49, 52], waist: [45, 48], hip: [54, 58] }
    },
    mens_jersey: {
      S:   { chest: [48, 48] },
      M:   { chest: [50, 50] },
      L:   { chest: [52, 52] },
      XL:  { chest: [54, 54] },
      XXL: { chest: [56, 56] },
      '3XL': { chest: [58, 58] },
      '4XL': { chest: [60, 60] }
    },
    mens_tee: {
      S:   { chest: [38, 40] },
      M:   { chest: [40, 42] },
      L:   { chest: [42, 44] },
      XL:  { chest: [44, 46] },
      XXL: { chest: [46, 48] }
    },
    oversized_tee: {
      S:   { chest: [38, 40] },
      M:   { chest: [42, 44] },
      L:   { chest: [46, 48] },
      XL:  { chest: [50, 52] },
      XXL: { chest: [54, 56] }
    }
  };

  var SIZE_LABELS = {
    'XS': ['X-Small', 'XS'], 'S': ['Small', 'S'], 'M': ['Medium', 'M'],
    'L': ['Large', 'L'], 'XL': ['X-Large', 'XL'], 'XXL': ['XXL', '2XL'],
    '1X': ['1X'], '2X': ['2X'], '3XL': ['3XL'], '4XL': ['4XL']
  };

  var SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '1X', '2X', '3XL', '4XL'];
  var DISPLAY_MAP = { XS: 'X-Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'X-Large' };

  // =========================================================================
  // Height + body shape → measurement estimates (no weight needed)
  // Lookup tables based on anthropometric data, keyed by height bracket.
  // Shape modifiers shift from the baseline (hourglass is baseline).
  // =========================================================================
  var HEIGHT_BRACKETS = [
    // [maxHeight, bust, waist, hip]  — baseline (hourglass) measurements
    [61,  32.0, 26.0, 35.0],   // under 5'2"
    [63,  33.0, 27.0, 36.5],   // 5'2"–5'3"
    [65,  34.0, 28.0, 37.5],   // 5'4"–5'5"
    [67,  35.0, 29.0, 38.5],   // 5'6"–5'7"
    [69,  36.0, 30.0, 40.0],   // 5'8"–5'9"
    [71,  37.0, 31.0, 41.0],   // 5'10"–5'11"
    [999, 38.0, 32.0, 42.0]    // 6'0"+
  ];

  var SHAPE_MODS = {
    hourglass: { bust: 0,    waist: -1.0, hip: 0 },
    pear:      { bust: -1.0, waist: 0,    hip: 2.0 },
    apple:     { bust: 1.5,  waist: 2.0,  hip: -1.0 },
    athletic:  { bust: -0.5, waist: -0.5, hip: -1.0 }
  };

  function estimateMeasurements(heightIn, shape) {
    // Find the right height bracket
    var base = HEIGHT_BRACKETS[HEIGHT_BRACKETS.length - 1];
    for (var i = 0; i < HEIGHT_BRACKETS.length; i++) {
      if (heightIn <= HEIGHT_BRACKETS[i][0]) { base = HEIGHT_BRACKETS[i]; break; }
    }
    var m = SHAPE_MODS[shape] || SHAPE_MODS.hourglass;
    return {
      bust:  Math.round((base[1] + m.bust) * 2) / 2,
      waist: Math.round((base[2] + m.waist) * 2) / 2,
      hip:   Math.round((base[3] + m.hip) * 2) / 2
    };
  }

  // =========================================================================
  // Size matching
  // =========================================================================
  function fitScore(value, range) {
    var min = range[0], max = range[1];
    if (value >= min && value <= max) return 1.0;
    var dist = value < min ? min - value : value - max;
    return Math.max(0, 1.0 - (dist / (max - min + 2)));
  }

  function matchSize(measurements, chartKey, fitDirection, stretch, fitPref) {
    var chart = CHARTS[chartKey] || CHARTS.womens;
    var isWomens = chartKey === 'womens';
    var scores = {};

    for (var size in chart) {
      var r = chart[size];
      var score = 0, total = 0;
      if (isWomens) {
        if (r.bust)  { score += fitScore(measurements.bust, r.bust) * 30; total += 30; }
        if (r.waist) { score += fitScore(measurements.waist, r.waist) * 35; total += 35; }
        if (r.hip)   { score += fitScore(measurements.hip, r.hip) * 35; total += 35; }
      } else {
        if (r.chest) { score += fitScore(measurements.bust, r.chest) * 100; total += 100; }
      }
      scores[size] = total > 0 ? score / total : 0;
    }

    var bestSize = null, bestScore = -1;
    for (var s in scores) {
      if (scores[s] > bestScore) { bestScore = scores[s]; bestSize = s; }
    }

    if (fitDirection === 'size_up') bestSize = sizeShift(bestSize, chart, 1);
    else if (fitDirection === 'leans_small' && fitPref !== 'snug') bestSize = sizeShift(bestSize, chart, 1);
    if (stretch <= 2 && fitDirection !== 'size_up') bestSize = sizeShift(bestSize, chart, 1);
    if (fitPref === 'snug') bestSize = sizeShift(bestSize, chart, -1) || bestSize;
    if (fitPref === 'roomy') bestSize = sizeShift(bestSize, chart, 1) || bestSize;

    return bestSize;
  }

  function sizeShift(current, chart, direction) {
    var keys = [];
    for (var k in chart) keys.push(k);
    keys.sort(function (a, b) { return SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b); });
    var idx = keys.indexOf(current);
    if (idx === -1) return current;
    var newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= keys.length) return current;
    return keys[newIdx];
  }

  // =========================================================================
  // localStorage profile
  // =========================================================================
  function getProfile() {
    try {
      var stored = localStorage.getItem(KEY);
      if (!stored) return null;
      var data = JSON.parse(stored);
      if (Date.now() - (data.ts || 0) > EXPIRY_DAYS * 86400000) {
        localStorage.removeItem(KEY);
        return null;
      }
      return data;
    } catch (e) { return null; }
  }

  function saveProfile(height, weight, shape, fitPref, measurements, usedCamera) {
    try {
      var profile = {
        h: height, b: shape, f: fitPref,
        m: measurements, v: 3, ts: Date.now()
      };
      if (usedCamera) profile.cam = true;
      localStorage.setItem(KEY, JSON.stringify(profile));
      // Clear quick pick — quiz is now the authority
      localStorage.removeItem('frw_quick_size');
    } catch (e) { /* degrade gracefully */ }
  }

  // =========================================================================
  // Variant picker integration
  // =========================================================================
  function selectVariant(sizeKey) {
    var picker = document.querySelector('variant-picker');
    if (!picker) return;
    var labels = SIZE_LABELS[sizeKey] || [sizeKey];
    var radios = picker.querySelectorAll('fieldset input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      var val = radios[i].value;
      for (var j = 0; j < labels.length; j++) {
        if (val === labels[j]) {
          radios[i].checked = true;
          radios[i].dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }

  // =========================================================================
  // Camera Body Scan V2 — Two-photo, real-time guidance, measurement overlay
  // =========================================================================
  var poseLandmarker = null;
  var cameraStream = null;
  var animFrameId = null;

  function loadPoseLandmarker(statusEl) {
    if (poseLandmarker) return Promise.resolve(poseLandmarker);
    if (statusEl) statusEl.textContent = 'Loading vision model\u2026';

    // Load MediaPipe via injected module script (works with Shopify CSP,
    // unlike dynamic import() which is blocked by script-src policy).
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.type = 'module';
      script.textContent =
        'import{PoseLandmarker as P,FilesetResolver as F}from"https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/+esm";' +
        'window.__mpVision={P:P,F:F};window.dispatchEvent(new Event("mp-ready"));';

      function onReady() {
        window.removeEventListener('mp-ready', onReady);
        var V = window.__mpVision;
        if (!V || !V.P || !V.F) { reject(new Error('MediaPipe module failed to export')); return; }

        if (statusEl) statusEl.textContent = 'Initializing pose detection\u2026';
        V.F.forVisionTasks('https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm')
          .then(function (vision) {
            return V.P.createFromOptions(vision, {
              baseOptions: {
                modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task',
                delegate: 'GPU'
              },
              runningMode: 'VIDEO',
              numPoses: 1
            });
          })
          .then(function (lm) { poseLandmarker = lm; resolve(lm); })
          .catch(reject);
      }

      window.addEventListener('mp-ready', onReady);
      script.onerror = function () { reject(new Error('Failed to load MediaPipe script')); };
      document.head.appendChild(script);

      // Timeout — if module doesn't load in 15s, reject
      setTimeout(function () { reject(new Error('MediaPipe load timeout')); }, 15000);
    });
  }

  function startCamera(videoEl) {
    return navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 960 } }
    }).then(function (stream) {
      cameraStream = stream;
      videoEl.srcObject = stream;
      return new Promise(function (res) {
        videoEl.onloadedmetadata = function () { res(videoEl); };
      });
    });
  }

  function stopCamera(videoEl) {
    if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }
    if (cameraStream) {
      cameraStream.getTracks().forEach(function (t) { t.stop(); });
      cameraStream = null;
    }
    if (videoEl) videoEl.srcObject = null;
  }

  function _dist(a, b) {
    var dx = a.x - b.x, dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function _mid(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }

  // Real-time pose quality check
  function checkPoseQuality(landmarks) {
    var checks = { ok: false, message: 'Step into frame' };
    if (!landmarks || landmarks.length === 0) return checks;

    var lm = landmarks[0];
    var nose = lm[0], lSh = lm[11], rSh = lm[12];
    var lHp = lm[23], rHp = lm[24], lAn = lm[27], rAn = lm[28];

    // Just need shoulders visible — most critical landmarks
    if ((lSh.visibility || 0) < 0.4 || (rSh.visibility || 0) < 0.4) {
      checks.message = 'Face the camera';
      return checks;
    }

    // Need hips visible at minimum (ankles optional — often low confidence)
    var hipVis = Math.min(lHp.visibility || 0, rHp.visibility || 0);
    if (hipVis < 0.3) {
      checks.message = 'Step back \u2014 show your full body';
      return checks;
    }

    // Use hips for body height if ankles aren't visible
    var ankleVis = Math.min(lAn.visibility || 0, rAn.visibility || 0);
    var bottomY = ankleVis > 0.2 ? (lAn.y + rAn.y) / 2 : (lHp.y + rHp.y) / 2;
    var bodyH = bottomY - nose.y;
    if (bodyH < 0.25) { checks.message = 'Step back so we can see more of you'; return checks; }
    if (bodyH > 0.90) { checks.message = 'Step back just a little'; return checks; }

    var midX = (lSh.x + rSh.x) / 2;
    if (midX < 0.15 || midX > 0.85) {
      checks.message = 'Move towards the center';
      return checks;
    }

    checks.ok = true;
    checks.message = 'Perfect \u2014 hold completely still!';
    return checks;
  }

  // Extract front-view measurements from landmarks
  function extractFrontData(landmarks, heightInches) {
    var lSh = landmarks[11], rSh = landmarks[12];
    var lHp = landmarks[23], rHp = landmarks[24];
    var lAn = landmarks[27], rAn = landmarks[28];
    var nose = landmarks[0];

    var ankleCenter = _mid(lAn, rAn);
    var bodyHeight = Math.abs(nose.y - ankleCenter.y);
    if (bodyHeight < 0.3) return null;

    var sRatio = _dist(lSh, rSh) / bodyHeight;
    var hRatio = _dist(lHp, rHp) / bodyHeight;

    return { sRatio: sRatio, hRatio: hRatio, bodyHeight: bodyHeight, landmarks: landmarks };
  }

  // Extract side-view depth data from landmarks
  function extractSideData(landmarks) {
    var lSh = landmarks[11], rSh = landmarks[12];
    var lHp = landmarks[23], rHp = landmarks[24];
    var lAn = landmarks[27], rAn = landmarks[28];
    var nose = landmarks[0];

    var ankleCenter = _mid(lAn, rAn);
    var bodyHeight = Math.abs(nose.y - ankleCenter.y);
    if (bodyHeight < 0.2) return null;

    // In profile, shoulder x-spread gives depth
    var chestDepth = Math.abs(lSh.x - rSh.x) / bodyHeight;
    var hipDepth = Math.abs(lHp.x - rHp.x) / bodyHeight;

    // Sanity check: depth should be smaller than front width
    if (chestDepth < 0.01 || hipDepth < 0.01) return null;

    return { chestDepth: chestDepth, hipDepth: hipDepth };
  }

  // Combine camera landmark data with height-based lookup tables.
  //
  // Strategy: Camera detects body SHAPE from shoulder/hip ratio, then we
  // use our calibrated height+shape tables as the base. Camera ratios
  // fine-tune ±2" from that baseline based on how the person's proportions
  // compare to the average for their detected shape.
  //
  // This is robust because:
  //   - Lookup tables are calibrated across the full size range (XS–2X)
  //   - Camera personalizes within ±2" rather than computing from scratch
  //   - Works across all body types, not just one reference person
  //
  // Average landmark ratios (shoulder_width / body_height) by shape:
  var AVG_RATIOS = {
    hourglass: { s: 0.23, h: 0.19 },
    pear:      { s: 0.21, h: 0.21 },
    apple:     { s: 0.25, h: 0.18 },
    athletic:  { s: 0.24, h: 0.18 }
  };

  function detectShapeFromCamera(sRatio, hRatio) {
    // Shoulder-to-hip ratio determines body shape
    var ratio = sRatio / (hRatio || 0.01);
    if (ratio > 1.35) return 'apple';      // shoulders much wider than hips
    if (ratio > 1.15) return 'athletic';   // shoulders wider, narrow hips
    if (ratio < 0.95) return 'pear';       // hips wider than shoulders
    return 'hourglass';                     // balanced
  }

  function computeMeasurements(frontData, sideData, heightInches) {
    // 1. Detect body shape from camera proportions
    var detectedShape = detectShapeFromCamera(frontData.sRatio, frontData.hRatio);

    // 2. Get baseline from calibrated lookup tables
    var base = estimateMeasurements(heightInches, detectedShape);

    // 3. Fine-tune with camera data: compare actual ratios to averages
    var avg = AVG_RATIOS[detectedShape];
    var sDiff = (frontData.sRatio - avg.s) / avg.s; // % deviation from average
    var hDiff = (frontData.hRatio - avg.h) / avg.h;

    // Scale adjustments: ±2" max per measurement
    var bustAdj  = Math.max(-2, Math.min(2, sDiff * 6));
    var hipAdj   = Math.max(-2, Math.min(2, hDiff * 6));
    var waistAdj = (bustAdj + hipAdj) * 0.4; // waist influenced by both

    var bust  = base.bust + bustAdj;
    var waist = base.waist + waistAdj;
    var hip   = base.hip + hipAdj;

    // Sanity bounds
    bust  = Math.max(28, Math.min(54, bust));
    waist = Math.max(22, Math.min(48, waist));
    hip   = Math.max(30, Math.min(56, hip));

    return {
      bust:  Math.round(bust * 2) / 2,
      waist: Math.round(waist * 2) / 2,
      hip:   Math.round(hip * 2) / 2
    };
  }

  // Draw body outline + measurement lines on canvas
  function drawMeasurementOverlay(ctx, landmarks, measurements, w, h) {
    ctx.clearRect(0, 0, w, h);
    if (!landmarks) return;

    var lSh = landmarks[11], rSh = landmarks[12];
    var lHp = landmarks[23], rHp = landmarks[24];

    ctx.save();

    // Body outline
    ctx.strokeStyle = 'rgba(125, 237, 159, 0.4)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(lSh.x * w, lSh.y * h);
    var waistY = (lSh.y * 0.4 + lHp.y * 0.6) * h;
    ctx.quadraticCurveTo(lSh.x * w - 8, waistY, lHp.x * w, lHp.y * h);
    ctx.lineTo(rHp.x * w, rHp.y * h);
    ctx.quadraticCurveTo(rSh.x * w + 8, waistY, rSh.x * w, rSh.y * h);
    ctx.closePath();
    ctx.stroke();

    // Measurement lines
    ctx.setLineDash([]);
    ctx.lineWidth = 1.5;

    var lines = [
      { y: (lSh.y + rSh.y) / 2, label: 'BUST', value: measurements.bust, color: '#7bed9f' },
      { y: (lSh.y * 0.4 + lHp.y * 0.6 + rSh.y * 0.4 + rHp.y * 0.6) / 2, label: 'WAIST', value: measurements.waist, color: '#74b9ff' },
      { y: (lHp.y + rHp.y) / 2, label: 'HIP', value: measurements.hip, color: '#fd79a8' }
    ];

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var ly = line.y * h;
      var x1 = Math.min(lSh.x, lHp.x) * w - 15;
      var x2 = Math.max(rSh.x, rHp.x) * w + 15;

      // Dashed line
      ctx.strokeStyle = line.color;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(x1, ly);
      ctx.lineTo(x2, ly);
      ctx.stroke();

      // Endpoint dots
      ctx.setLineDash([]);
      ctx.fillStyle = line.color;
      ctx.beginPath();
      ctx.arc(x1, ly, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x2, ly, 3, 0, Math.PI * 2);
      ctx.fill();

      // Label
      ctx.fillStyle = 'rgba(0,0,0,.6)';
      ctx.fillRect(x2 + 6, ly - 12, 48, 24);
      ctx.fillStyle = '#fff';
      ctx.font = '600 11px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(line.value + '"', x2 + 10, ly + 4);
      ctx.fillStyle = 'rgba(255,255,255,.5)';
      ctx.font = '500 8px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(line.label, x2 + 10, ly - 3);
    }

    ctx.restore();
  }

  // =========================================================================
  // UI Controller
  // =========================================================================
  function init() {
    var root = document.getElementById('pf-root');
    if (!root) return;

    var chartKey = root.dataset.chart || 'womens';
    var fitDirection = root.dataset.rec || 'true_to_size';
    var stretch = parseInt(root.dataset.stretch, 10) || 3;
    var fabric = root.dataset.fabric || '';

    var trigger = document.getElementById('pf-trigger');
    var badge = document.getElementById('pf-badge');
    var badgeSize = document.getElementById('pf-badge-size');
    var dialog = document.getElementById('pf-dialog');
    var closeBtn = document.getElementById('pf-close');
    var applyBtn = document.getElementById('pf-apply');
    var retakeInner = document.getElementById('pf-retake-inner');
    var progressEl = document.getElementById('pf-progress');
    var resultSizeEl = document.getElementById('pf-result-size');
    var resultContextEl = document.getElementById('pf-result-context');

    var screens = root.querySelectorAll('.pf-screen');
    var screenOrder = ['choose', 'height', 'shape', 'review', 'fit'];
    var currentScreen = 0;

    // Quiz state
    var quizData = { height: 65, shape: null, fit: null, measurements: null };
    var computedSize = null;
    var cameraMode = false;
    var frontData = null;
    var sideData = null;
    var capturedFrame = null;

    // DOM refs — camera
    var videoEl = document.getElementById('pf-video');
    var videoElSide = document.getElementById('pf-video-side');
    var cameraWrap = document.getElementById('pf-camera-wrap');
    var cameraWrapSide = document.getElementById('pf-camera-wrap-side');
    var poseHint = document.getElementById('pf-pose-hint');
    var poseHintSide = document.getElementById('pf-pose-hint-side');
    var captureFrontBtn = document.getElementById('pf-capture-front');
    var captureSideBtn = document.getElementById('pf-capture-side');
    var skipSideBtn = document.getElementById('pf-skip-side');
    var scanStatus = document.getElementById('pf-scan-status');
    var reviewCanvas = document.getElementById('pf-review-canvas');
    var confirmMeasBtn = document.getElementById('pf-confirm-measurements');
    var reviewPreview = document.getElementById('pf-review-preview');
    var feetSelect = document.getElementById('pf-feet');

    if (feetSelect) {
      feetSelect.addEventListener('change', function () {
        quizData.height = parseInt(this.value, 10);
      });
    }

    // Shape selection
    var shapeButtons = root.querySelectorAll('.pf-shape');
    shapeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        shapeButtons.forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        quizData.shape = btn.dataset.shape;
        var nextBtn = btn.closest('.pf-screen').querySelector('.pf-next');
        if (nextBtn) nextBtn.disabled = false;
      });
    });

    // Fit preference (auto-advances to result)
    var fitButtons = root.querySelectorAll('.pf-fit-opt');
    fitButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        fitButtons.forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        quizData.fit = btn.dataset.fit;
        computeAndShowResult();
      });
    });

    // =======================================================================
    // Camera V2 flow
    // =======================================================================
    var useCameraBtn = document.getElementById('pf-choose-scan');
    var useQuizBtn = document.getElementById('pf-choose-quiz');

    // "Quick Quiz" → go to height screen
    if (useQuizBtn) {
      useQuizBtn.addEventListener('click', function () {
        track('frw_perfect_fit_start', { method: 'quiz' });
        showScreen(1); // height
      });
    }
    var poseGoodSince = 0;
    var poseGoodSinceSide = 0;

    function startDetectLoop(vid, hintEl, wrapEl, captBtn, isSide) {
      var goodSince = 0;
      var countdownStart = 0;
      var countdownEl = document.getElementById(isSide ? 'pf-countdown-side' : 'pf-countdown');
      var autoFired = false;

      function loop(ts) {
        if (!cameraStream || autoFired) return;
        try {
          var results = poseLandmarker.detectForVideo(vid, ts);
          var quality = checkPoseQuality(results.landmarks);

          if (hintEl) hintEl.textContent = quality.message;
          if (wrapEl) wrapEl.classList.toggle('pf-pose-good', quality.ok);

          if (quality.ok) {
            if (!goodSince) goodSince = ts;
            if (captBtn) captBtn.disabled = false;

            // Auto-capture countdown (starts after 500ms of good pose)
            if (ts - goodSince > 500) {
              if (!countdownStart) countdownStart = ts;
              var remaining = Math.ceil(3 - (ts - countdownStart) / 1000);
              if (remaining > 0) {
                if (countdownEl) { countdownEl.style.display = ''; countdownEl.textContent = remaining; }
                if (hintEl) hintEl.textContent = 'Hold still\u2026';
              } else {
                // Auto-capture
                autoFired = true;
                if (countdownEl) countdownEl.style.display = 'none';
                if (captBtn) captBtn.click();
                return;
              }
            }
          } else {
            goodSince = 0;
            countdownStart = 0;
            if (countdownEl) countdownEl.style.display = 'none';
            if (captBtn) captBtn.disabled = true;
          }
        } catch (e) { /* skip frame */ }

        animFrameId = requestAnimationFrame(loop);
      }
      // Model already loaded by camera button handler
      if (poseLandmarker) {
        animFrameId = requestAnimationFrame(loop);
      }
    }

    var pendingCamera = false;

    // "Body Scan" → show height first, then launch camera after Next
    if (useCameraBtn) {
      useCameraBtn.addEventListener('click', function () {
        pendingCamera = true;
        track('frw_camera_used', { method: 'camera' });
        showScreen(1); // height
      });
    }

    function launchCamera() {
      cameraMode = true;
      pendingCamera = false;
      quizData.height = parseInt(feetSelect.value, 10);

      screens.forEach(function (s) { s.style.display = 'none'; });
      var scanScreen = root.querySelector('[data-screen="scanning"]');
      if (scanScreen) scanScreen.style.display = '';
      if (progressEl) progressEl.style.display = 'none';

      Promise.all([
        loadPoseLandmarker(scanStatus),
        startCamera(videoEl)
      ]).then(function () {
        screens.forEach(function (s) { s.style.display = 'none'; });
        var cam = root.querySelector('[data-screen="camera-front"]');
        if (cam) cam.style.display = '';
        startDetectLoop(videoEl, poseHint, cameraWrap, captureFrontBtn, false);
      }).catch(function (err) {
        cameraMode = false;
        if (progressEl) progressEl.style.display = '';
        console.error('[Perfect Fit] Camera init failed:', err);
        if (scanStatus) {
          var msg = (err && err.name === 'NotAllowedError')
            ? 'Camera permission denied — allow camera access and try again'
            : 'Camera not available — try the quiz instead';
          scanStatus.textContent = msg;
          scanStatus.style.color = '#ff6b6b';
        }
        setTimeout(function () { showScreen(0); }, 2500);
      });
    }

    // Front capture
    if (captureFrontBtn) {
      captureFrontBtn.addEventListener('click', function () {
        captureFrontBtn.disabled = true;
        if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }

        // Capture frame for review overlay
        var tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = videoEl.videoWidth;
        tmpCanvas.height = videoEl.videoHeight;
        tmpCanvas.getContext('2d').drawImage(videoEl, 0, 0);
        capturedFrame = tmpCanvas;

        // Detect on captured frame
        var result = poseLandmarker.detectForVideo(videoEl, performance.now());
        if (!result.landmarks || result.landmarks.length === 0) {
          if (poseHint) poseHint.textContent = 'Lost detection \u2014 try again';
          captureFrontBtn.disabled = false;
          startDetectLoop(videoEl, poseHint, cameraWrap, captureFrontBtn, false);
          return;
        }

        frontData = extractFrontData(result.landmarks[0], quizData.height);
        if (!frontData) {
          if (poseHint) poseHint.textContent = 'Partial detection \u2014 try again';
          captureFrontBtn.disabled = false;
          startDetectLoop(videoEl, poseHint, cameraWrap, captureFrontBtn, false);
          return;
        }

        // Brief confirmation before side transition
        if (poseHint) poseHint.textContent = 'Front photo captured!';
        if (cameraWrap) cameraWrap.classList.remove('pf-pose-good');

        // Show transition instruction after 1.5s pause
        setTimeout(function () {
          screens.forEach(function (s) { s.style.display = 'none'; });
          var sideScreen = root.querySelector('[data-screen="camera-side"]');
          if (sideScreen) sideScreen.style.display = '';

          // Show big instruction, delay detection start so they can read + turn
          if (poseHintSide) poseHintSide.textContent = 'Turn to your left, hands down';
          if (videoElSide) {
            videoElSide.srcObject = cameraStream;
            videoElSide.play();
          }
          setTimeout(function () {
            if (!cameraStream) return;
            if (poseHintSide) poseHintSide.textContent = 'Hands down by your side';
            startDetectLoop(videoElSide, poseHintSide, cameraWrapSide, captureSideBtn, true);
          }, 3500);
        }, 1500);
      });
    }

    // Side capture
    if (captureSideBtn) {
      captureSideBtn.addEventListener('click', function () {
        captureSideBtn.disabled = true;
        if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }

        var result = poseLandmarker.detectForVideo(videoElSide, performance.now());
        if (result.landmarks && result.landmarks.length > 0) {
          sideData = extractSideData(result.landmarks[0]);
        }
        finishCapture();
      });
    }

    // Skip side
    if (skipSideBtn) {
      skipSideBtn.addEventListener('click', function () {
        if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }
        sideData = null;
        finishCapture();
      });
    }

    function finishCapture() {
      stopCamera(videoEl);
      if (videoElSide) videoElSide.srcObject = null;

      // Show scanning animation briefly while we process
      screens.forEach(function (s) { s.style.display = 'none'; });
      var scanScreen = root.querySelector('[data-screen="scanning"]');
      if (scanScreen) scanScreen.style.display = '';
      if (scanStatus) { scanStatus.textContent = 'Calculating your measurements\u2026'; scanStatus.style.color = ''; }

      setTimeout(function () { finishCaptureInner(); }, 2000);
    }

    function finishCaptureInner() {
      if (!frontData) {
        resetCamera();
        showScreen(0);
        return;
      }

      var meas = computeMeasurements(frontData, sideData, quizData.height);
      quizData.measurements = meas;

      // Show measurement review screen
      showCameraScreen('review');
      populateReview(meas);
    }

    function populateReview(meas) {
      var headerEl = document.getElementById('pf-review-header');
      var titleEl = document.getElementById('pf-review-title');
      var hintEl = document.getElementById('pf-review-hint');

      // Camera path: show compact thumbnail header, hide quiz title/hint
      if (cameraMode && capturedFrame && reviewCanvas) {
        if (headerEl) headerEl.style.display = '';
        if (titleEl) titleEl.style.display = 'none';
        if (hintEl) hintEl.style.display = 'none';
        // Draw captured frame into small circular thumbnail
        reviewCanvas.width = capturedFrame.width;
        reviewCanvas.height = capturedFrame.height;
        var ctx = reviewCanvas.getContext('2d');
        ctx.drawImage(capturedFrame, 0, 0);
      } else {
        // Quiz path: show title + hint, hide camera header
        if (headerEl) headerEl.style.display = 'none';
        if (titleEl) titleEl.style.display = '';
        if (hintEl) hintEl.style.display = '';
      }

      // Scroll wheel pickers to measurement values
      var fields = ['bust', 'waist', 'hip'];
      for (var i = 0; i < fields.length; i++) {
        var f = fields[i];
        var v = Math.round(meas[f] * 2) / 2; // snap to nearest half inch
        meas[f] = v;
        if (wheelEls[f]) scrollWheelTo(wheelEls[f], v);
      }

      // Show size preview
      updateReviewPreview();
    }

    function updateReviewPreview() {
      if (!quizData.measurements || !reviewPreview) return;
      var previewSize = matchSize(quizData.measurements, chartKey, fitDirection, stretch, 'tts');
      reviewPreview.textContent = 'Recommended: ' + (DISPLAY_MAP[previewSize] || previewSize);
    }

    // Measurement wheel pickers
    var ITEM_H = 40; // must match CSS .pf-wheel-item height

    function buildWheel(wheelEl, min, max, step) {
      // Pad top/bottom so first and last items can center
      var pad = document.createElement('div');
      pad.style.height = ITEM_H + 'px';
      wheelEl.appendChild(pad);
      for (var v = min; v <= max; v += step) {
        var item = document.createElement('div');
        item.className = 'pf-wheel-item';
        item.dataset.val = v;
        item.textContent = (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) + '"';
        wheelEl.appendChild(item);
      }
      var padB = document.createElement('div');
      padB.style.height = ITEM_H + 'px';
      wheelEl.appendChild(padB);
    }

    function scrollWheelTo(wheelEl, value) {
      var items = wheelEl.querySelectorAll('.pf-wheel-item');
      for (var i = 0; i < items.length; i++) {
        if (parseFloat(items[i].dataset.val) === value) {
          wheelEl.scrollTop = i * ITEM_H;
          updateWheelActive(wheelEl);
          return;
        }
      }
    }

    function updateWheelActive(wheelEl) {
      var center = wheelEl.scrollTop + ITEM_H; // center of visible area (accounting for top pad)
      var items = wheelEl.querySelectorAll('.pf-wheel-item');
      var closest = null, closestDist = Infinity;
      items.forEach(function (item) {
        var mid = item.offsetTop - wheelEl.querySelector('.pf-wheel-item').parentElement.children[0].offsetHeight + ITEM_H / 2;
        var dist = Math.abs(item.offsetTop - wheelEl.scrollTop - ITEM_H + ITEM_H / 2);
        item.classList.remove('pf-wheel-active');
        if (dist < closestDist) { closestDist = dist; closest = item; }
      });
      if (closest) closest.classList.add('pf-wheel-active');
      return closest ? parseFloat(closest.dataset.val) : null;
    }

    function getWheelValue(wheelEl) {
      return updateWheelActive(wheelEl);
    }

    // Build all three wheels
    var wheelEls = {};
    ['bust', 'waist', 'hip'].forEach(function (key) {
      var card = root.querySelector('.pf-meas-card[data-measure="' + key + '"]');
      var wheelEl = document.getElementById('pf-wheel-' + key);
      if (!card || !wheelEl) return;
      var min = parseInt(card.dataset.min, 10);
      var max = parseInt(card.dataset.max, 10);
      buildWheel(wheelEl, min, max, 0.5);
      wheelEls[key] = wheelEl;

      var scrollTimer = null;
      wheelEl.addEventListener('scroll', function () {
        updateWheelActive(wheelEl);
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var val = getWheelValue(wheelEl);
          if (val != null && quizData.measurements) {
            quizData.measurements[key] = val;
            updateReviewPreview();
            // Redraw overlay (camera path)
            if (reviewCanvas && capturedFrame && frontData) {
              var ctx = reviewCanvas.getContext('2d');
              ctx.drawImage(capturedFrame, 0, 0);
              drawMeasurementOverlay(ctx, frontData.landmarks, quizData.measurements, capturedFrame.width, capturedFrame.height);
            }
            track('frw_measurement_edited', { measurement: key, value: val });
          }
        }, 120);
      });
    });

    // Sync number inputs with scroll wheels (keyboard accessibility)
    ['bust', 'waist', 'hip'].forEach(function (key) {
      var numInput = document.getElementById('pf-input-' + key);
      if (!numInput || !wheelEls[key]) return;
      // Wheel scroll → update number input
      wheelEls[key].addEventListener('scroll', function () {
        var val = getWheelValue(wheelEls[key]);
        if (val != null) numInput.value = (val % 1 === 0 ? val.toFixed(0) : val.toFixed(1));
      });
      // Number input → scroll wheel to match
      numInput.addEventListener('change', function () {
        var val = parseFloat(numInput.value);
        if (isNaN(val)) return;
        var min = parseInt(numInput.min, 10);
        var max = parseInt(numInput.max, 10);
        val = Math.max(min, Math.min(max, val));
        numInput.value = (val % 1 === 0 ? val.toFixed(0) : val.toFixed(1));
        scrollWheelTo(wheelEls[key], val);
        if (quizData.measurements) {
          quizData.measurements[key] = val;
          updateReviewPreview();
        }
      });
    });

    // Confirm measurements → go to fit preference
    if (confirmMeasBtn) {
      confirmMeasBtn.addEventListener('click', function () {
        if (progressEl) progressEl.style.display = '';
        showScreen(4); // fit preference
      });
    }

    function showCameraScreen(name) {
      screens.forEach(function (s) { s.style.display = 'none'; });
      var target = root.querySelector('[data-screen="' + name + '"]');
      if (target) target.style.display = '';
      if (progressEl) progressEl.style.display = 'none';
    }

    function resetCamera() {
      cameraMode = false;
      frontData = null;
      sideData = null;
      capturedFrame = null;
      quizData.measurements = null;
      if (captureFrontBtn) captureFrontBtn.disabled = true;
      if (captureSideBtn) captureSideBtn.disabled = true;
      if (progressEl) progressEl.style.display = '';
    }

    // =======================================================================
    // Screen navigation (quiz)
    // =======================================================================
    function showScreen(index) {
      screens.forEach(function (s) { s.style.display = 'none'; });
      if (index < screenOrder.length) {
        var screenName = screenOrder[index];
        var target = root.querySelector('[data-screen="' + screenName + '"]');
        if (target) target.style.display = '';
        currentScreen = index;
        updateProgress(index);

        // Pre-fill measurements when entering review screen from quiz
        if (screenName === 'review' && !cameraMode) {
          var meas = estimateMeasurements(quizData.height, quizData.shape || 'hourglass');
          quizData.measurements = meas;
          populateReview(meas);
        }
      }
    }

    function updateProgress(index) {
      if (!progressEl) return;
      // Hide dots on choose screen (0) and result screen
      if (index === 0 || index >= screenOrder.length) {
        progressEl.style.display = 'none';
        return;
      }
      progressEl.style.display = '';
      var dots = progressEl.querySelectorAll('.pf-dot');
      // Offset by 1 since choose screen doesn't count
      dots.forEach(function (d, i) {
        d.classList.toggle('pf-dot--active', i <= (index - 1));
      });
    }

    function computeAndShowResult() {
      var measurements = quizData.measurements || estimateMeasurements(quizData.height, quizData.shape || 'hourglass');
      computedSize = matchSize(measurements, chartKey, fitDirection, stretch, quizData.fit || 'tts');

      saveProfile(quizData.height, null, quizData.shape, quizData.fit, measurements, cameraMode);

      screens.forEach(function (s) { s.style.display = 'none'; });
      var resultScreen = root.querySelector('[data-screen="result"]');
      if (resultScreen) resultScreen.style.display = '';
      updateProgress(screenOrder.length);

      var displaySize = DISPLAY_MAP[computedSize] || computedSize;
      if (resultSizeEl) resultSizeEl.textContent = displaySize;
      if (resultContextEl) {
        var dirLabels = { size_up: 'runs small — we sized up for you', true_to_size: 'true to size', leans_small: 'leans slightly small' };
        resultContextEl.textContent = dirLabels[fitDirection] || '';
      }

      track('frw_perfect_fit_complete', {
        size: computedSize,
        method: cameraMode ? 'camera' : 'quiz',
        chart: chartKey
      });
    }

    // Navigation
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('.pf-next');
      if (btn && !btn.disabled) {
        // If on height screen and camera path was chosen, launch camera
        var parentScreen = btn.closest('.pf-screen');
        if (parentScreen && parentScreen.dataset.screen === 'height' && pendingCamera) {
          launchCamera();
          return;
        }
        showScreen(currentScreen + 1);
        return;
      }
      btn = e.target.closest('.pf-prev');
      if (btn) {
        var ps = btn.closest('.pf-screen');
        if (ps) {
          var sn = ps.dataset.screen;
          if (sn === 'camera-front' || sn === 'camera-side') {
            stopCamera(videoEl);
            if (videoElSide) videoElSide.srcObject = null;
            resetCamera();
            showScreen(0);
            return;
          }
          if (sn === 'review' && cameraMode) {
            stopCamera(videoEl);
            if (videoElSide) videoElSide.srcObject = null;
            resetCamera();
            showScreen(0);
            return;
          }
          if (sn === 'fit' && cameraMode) {
            // Go back to review if camera mode
            showCameraScreen('review');
            if (quizData.measurements) populateReview(quizData.measurements);
            return;
          }
        }
        showScreen(currentScreen - 1);
        return;
      }
    });

    // Open modal
    function openDialog() {
      if (!dialog) return;
      dialog.showModal();
      showScreen(0);
      shapeButtons.forEach(function (b) { b.classList.remove('active'); });
      fitButtons.forEach(function (b) { b.classList.remove('active'); });
      var shapeNext = root.querySelector('[data-screen="shape"] .pf-next');
      if (shapeNext) shapeNext.disabled = true;
      quizData.shape = null;
      quizData.fit = null;
      quizData.measurements = null;
      cameraMode = false;
      pendingCamera = false;
      frontData = null;
      sideData = null;
      capturedFrame = null;
      stopCamera(videoEl);
      if (videoElSide) videoElSide.srcObject = null;
      if (captureFrontBtn) captureFrontBtn.disabled = true;
      if (captureSideBtn) captureSideBtn.disabled = true;
      if (progressEl) progressEl.style.display = 'none';
      track('frw_perfect_fit_start', {});
    }

    function closeDialog() {
      stopCamera(videoEl);
      if (videoElSide) videoElSide.srcObject = null;
      if (dialog) dialog.close();
    }

    function applyResult() {
      if (computedSize) {
        selectVariant(computedSize);
        showBadge(computedSize);
        track('frw_perfect_fit_applied', { size: computedSize });
        // Notify FYF bar (collection pages) to refresh with the new profile
        document.dispatchEvent(new CustomEvent('frw:perfect-fit-complete', { detail: { size: computedSize } }));
      }
      closeDialog();
    }

    function showBadge(sizeKey) {
      var display = DISPLAY_MAP[sizeKey] || sizeKey;
      if (trigger) trigger.style.display = 'none';
      if (badge) {
        badge.style.display = 'flex';
        if (badgeSize) badgeSize.textContent = display;
      }
      // Cache computed size for instant inline render on next page load
      try {
        var pf = JSON.parse(localStorage.getItem(KEY) || '{}');
        if (pf && typeof pf === 'object') { pf.sz = sizeKey; localStorage.setItem(KEY, JSON.stringify(pf)); }
      } catch (e) { /* ignore */ }
    }

    // Allow external triggers (e.g., Find Your Fit quiz link on collection pages)
    document.addEventListener('frw:open-perfect-fit', openDialog);

    // Event listeners
    if (trigger) trigger.addEventListener('click', openDialog);
    if (closeBtn) closeBtn.addEventListener('click', closeDialog);
    if (applyBtn) applyBtn.addEventListener('click', applyResult);
    if (badge) badge.addEventListener('click', openDialog);
    if (retakeInner) retakeInner.addEventListener('click', function () { showScreen(0); });

    // Size chart link — scrolls to and opens the Size accordion
    var sizeChartLink = document.getElementById('pf-size-chart-link');
    if (sizeChartLink) {
      sizeChartLink.addEventListener('click', function (e) {
        e.preventDefault();
        closeDialog();
        var rows = document.querySelectorAll('accordion-custom');
        var target = null;
        for (var i = 0; i < rows.length; i++) {
          var d = rows[i].querySelector('details');
          if (!d) continue;
          var s = rows[i].querySelector('summary');
          if (s && s.textContent.indexOf('Size') !== -1) { target = rows[i]; d.open = true; }
          else { d.open = false; }
        }
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }

    if (dialog) {
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) closeDialog();
      });
    }

    // Check for quick size pick (from Find Your Fit) first, then quiz profile
    var quickSizeData = null;
    try {
      var qs = localStorage.getItem('frw_quick_size');
      if (qs) {
        quickSizeData = JSON.parse(qs);
        if (Date.now() - (quickSizeData.ts || 0) > EXPIRY_DAYS * 86400000) quickSizeData = null;
      }
    } catch (e) { /* ignore */ }

    var shown = false;
    if (quickSizeData && quickSizeData.size) {
      showBadge(quickSizeData.size);
      selectVariant(quickSizeData.size);
      shown = true;
    } else {
      var profile = getProfile();
      if (profile && profile.m) {
        var savedSize = matchSize(profile.m, chartKey, fitDirection, stretch, profile.f || 'tts');
        if (savedSize) {
          showBadge(savedSize);
          selectVariant(savedSize);
          shown = true;
        }
      }
    }
    if (!shown && trigger) trigger.style.display = 'flex';
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
